/**
 * Local mirror of the Relay API's wire types.
 *
 * These are deliberately duplicated rather than imported from `packages/shared`:
 * that package is Node-only (mongodb, bullmq, pino) and pulling it into a browser
 * bundle would drag the whole server runtime with it. It also models documents
 * (`_id`, `Date`) rather than JSON (`id`, ISO strings), so the shapes genuinely
 * differ. All timestamps below are ISO-8601 strings.
 */

export type DeliveryStatus = 'pending' | 'succeeded' | 'dead_lettered';

export type AttemptStatus = 'succeeded' | 'failed';

export type FailureKind = 'http_status' | 'timeout' | 'connection_error' | 'unknown';

/** One outbound HTTP request for one delivery. */
export interface Attempt {
  id: string;
  deliveryId: string;
  attemptNumber: number;
  status: AttemptStatus;
  responseStatus: number | null;
  failureKind: FailureKind | null;
  errorMessage: string | null;
  /** How long the subscriber took to respond. */
  requestDurationMs: number;
  /** Event ingestion through to the end of this attempt, including backoff waits. */
  elapsedSinceEventMs: number;
  startedAt: string;
  finishedAt: string;
}

/** One event aimed at one endpoint. Fan-out creates several per event. */
export interface Delivery {
  id: string;
  eventId: string;
  endpointId: string;
  url: string;
  status: DeliveryStatus;
  attemptCount: number;
  maxAttempts: number;
  /** Ingestion to successful delivery. Null unless the delivery succeeded. */
  latencyMs: number | null;
  lastError: string | null;
  nextAttemptAt: string | null;
  createdAt: string;
  completedAt: string | null;
}

export type DeliveryWithAttempts = Delivery & { attempts: Attempt[] };

export interface RelayEvent {
  id: string;
  eventType: string;
  payload: unknown;
  idempotencyKey: string | null;
  benchmarkRunId: string | null;
  createdAt: string;
}

/**
 * A row in `GET /events`. The contract names the element type `EventSummary` but
 * does not spell out its fields; the feed needs the per-event delivery rollup
 * ("2/3 delivered"), so we assume the event fields plus its deliveries.
 */
export interface EventSummary {
  id: string;
  eventType: string;
  benchmarkRunId: string | null;
  createdAt: string;
  deliveries: Delivery[];
}

export interface EventsPage {
  data: EventSummary[];
  nextCursor: string | null;
}

export interface EventDetail {
  event: RelayEvent;
  deliveries: DeliveryWithAttempts[];
}

export interface Endpoint {
  id: string;
  url: string;
  description: string | null;
  /** Empty means "every event type". */
  eventTypes: string[];
  disabled: boolean;
  createdAt: string;
}

/** Only ever returned by `POST /endpoints`; the secret is never readable again. */
export interface CreatedEndpoint extends Endpoint {
  secret: string;
}

export interface CreateEventResponse {
  eventId: string;
  deliveries: Array<{ id: string; endpointId: string; status: DeliveryStatus }>;
}

/**
 * The contract does not state whether `mongo`/`redis` are booleans or strings
 * like "ok", so both are accepted and normalised at the render site.
 */
export interface Health {
  status: string;
  mongo?: string | boolean | null;
  redis?: string | boolean | null;
}

export interface RetryDistributionBucket {
  attemptNumber: number;
  succeeded: number;
  /** Fraction of successful deliveries settled on this attempt, 0..1. */
  share: number;
}

export interface Stats {
  totals: { events: number; deliveries: number; attempts: number };
  /** Both 0..1. The gap between them is the point of the whole project. */
  successRate: { firstAttempt: number; eventual: number };
  deadLetterRate: number;
  latencyMs: { p50: number; p95: number; p99: number; mean: number };
  retryDistribution: RetryDistributionBucket[];
  attemptOutcomes: { succeeded: number; failed: number };
  failureKinds: Record<FailureKind, number>;
}

export const DELIVERY_STATUSES: DeliveryStatus[] = ['pending', 'succeeded', 'dead_lettered'];
